import React, { useState } from 'react';
import axios from 'axios';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import AuthLayout from './landing/AuthLayout';

/*
 * Main-app reset-password page (v2 auth chrome). The emailed link lands here
 * with ?token=. Validation mirrors the backend exactly (length >= 8, one
 * uppercase, one digit). On success we send the user to /login?reset=success
 * with replace, so the single-use token URL is not left in history.
 */
const requirementCheckers = {
  minLength: (pw) => pw.length >= 8,
  hasUppercase: (pw) => /[A-Z]/.test(pw),
  hasNumber: (pw) => /[0-9]/.test(pw),
};
const REQUIREMENT_KEYS = ['minLength', 'hasUppercase', 'hasNumber'];

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [tokenError, setTokenError] = useState(null); // null | 'invalid' | 'expired'
  const [error, setError] = useState('');

  const backendUrl = window.__BACKEND_URL || process.env.REACT_APP_BACKEND_URL;

  const requirements = REQUIREMENT_KEYS.reduce((acc, key) => {
    acc[key] = requirementCheckers[key](password);
    return acc;
  }, {});
  const allRequirementsMet = REQUIREMENT_KEYS.every((k) => requirements[k]);
  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const canSubmit = allRequirementsMet && passwordsMatch && !submitting;

  const showTokenErrorView = !token || tokenError !== null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (!canSubmit) return;
    if (!backendUrl) { setError(t('terminal.resetPassword.errors.backendUnavailable', 'The server is unavailable. Please try again shortly.')); return; }

    try {
      setSubmitting(true);
      await axios.post(
        `${backendUrl}/auth/reset-password`,
        { token, password },
        { withCredentials: true }
      );
      navigate('/login?reset=success', { replace: true });
    } catch (err) {
      const status = err.response?.status;
      const message = err.response?.data?.message || '';
      if (status === 400 && /expired/i.test(message)) {
        setTokenError('expired');
      } else if (status === 400 && /token/i.test(message)) {
        setTokenError('invalid');
      } else {
        setError(t('terminal.resetPassword.genericError', 'Something went wrong. Please try again.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (showTokenErrorView) {
    const msg = tokenError === 'expired'
      ? t('terminal.resetPassword.tokenExpired', 'This reset link has expired. Please request a new one.')
      : t('terminal.resetPassword.tokenInvalid', 'This reset link is invalid or has expired. Please request a new one.');
    return (
      <AuthLayout>
        <SEO title="Set new password" path="/reset-password" noindex />
        <div className="auth-center">
          <div className="auth-success-icon" aria-hidden="true" style={{ color: 'var(--red)' }}>✕</div>
          <h1 className="auth-title">{t('terminal.resetPassword.title', 'Set new password')}</h1>
          <p className="auth-sub">{msg}</p>
          <RouterLink to="/forgot-password" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
            {t('terminal.resetPassword.requestNewLink', 'Request new link')}
          </RouterLink>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <SEO title="Set new password" path="/reset-password" noindex />

      <h1 className="auth-title">{t('terminal.resetPassword.title', 'Set new password')}</h1>
      <p className="auth-sub">{t('terminal.resetPassword.description', 'Choose a new password for your account.')}</p>

      {error && <div className="auth-alert error" role="alert">{error}</div>}

      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <div className="field">
          <label htmlFor="rp-password">{t('terminal.resetPassword.passwordLabel', 'New password')}</label>
          <input
            id="rp-password"
            name="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="new-password"
            autoFocus
          />
        </div>

        <div className="auth-reqs">
          {REQUIREMENT_KEYS.map((key) => (
            <div className={`auth-req${requirements[key] ? ' met' : ''}`} key={key}>
              <span className="auth-req-mark">{requirements[key] ? '✓' : '○'}</span>
              {t(`terminal.resetPassword.requirements.${key}`, {
                defaultValue: { minLength: 'At least 8 characters', hasUppercase: 'At least one uppercase letter', hasNumber: 'At least one number' }[key],
              })}
            </div>
          ))}
        </div>

        <div className="field">
          <label htmlFor="rp-confirm">{t('terminal.resetPassword.confirmPasswordLabel', 'Confirm password')}</label>
          <input
            id="rp-confirm"
            name="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            autoComplete="new-password"
          />
          {confirmPassword.length > 0 && (
            <span className={`auth-req${passwordsMatch ? ' met' : ''}`} style={{ marginTop: 2 }}>
              <span className="auth-req-mark">{passwordsMatch ? '✓' : '○'}</span>
              {passwordsMatch
                ? t('terminal.resetPassword.passwordsMatch', 'Passwords match')
                : t('terminal.resetPassword.passwordsDontMatch', "Passwords don't match")}
            </span>
          )}
        </div>

        <button type="submit" className="btn btn-primary" disabled={!canSubmit} aria-busy={submitting}>
          {submitting ? <span className="spinner" /> : t('terminal.resetPassword.submitButton', 'Reset password')}
        </button>
      </form>

      <p className="auth-switch">
        <RouterLink to="/login">{t('terminal.forgotPassword.backToSignIn', 'Back to sign in')}</RouterLink>
      </p>
    </AuthLayout>
  );
};

export default ResetPassword;
